import moment from 'moment';
import { difference, isEmpty, pullAll } from 'lodash';

import {
  CallLogFilter,
  dateFormat,
  getEndDate,
  isBetweenDates,
  QLog,
  QMessageMetadata,
  QTask,
  QTaskFilter,
  TaskPriority,
  TaskStatus,
} from '@qwealth/qdata';

import {
  ActivityFilters,
  defaultFilter,
  ACTIVITY_FILTER_KEY,
  ActivityFiltersWithoutQuery,
  ALL_LOG_SUBJECTS,
  CALL_LOG_FILTERS_KEY,
  defaultCallLogFilters,
  LogFilterParam,
  TASK_FILTERS_KEY,
  defaultTaskFilters,
  TaskFilterParam,
  QFeedTaskFilterParam,
  UNTAGGED_TASKS,
} from 'data/models/ActivityFilters';
import { DateRange } from 'data/models/DateRange';
import { flattenList } from './DataUtil';
import { caseInsensitiveFuzzySearch } from './search';
import { ENG_DATE_FORMAT } from 'data/refData/time';
import { CalendarEvent } from 'data/data-layer/event';
import { SortDirection } from 'data/models/SortDirection';
import { Activity } from 'data/models/Activity';
import { ALL_DATE_RANGE, ASSIGN_TO_ME_RESPONSIBILITY, CREATED_BY_ME_RESPONSIBILITY } from 'data/refData/acitivites';
import { getAllTasks } from 'data/data-layer/qtask';
import { getPartnerTaskTags } from 'data/actions/taskTags';
import { AppThunkDispatch } from 'data/store';

export const getSavedFilters = (): ActivityFilters => {
  const savedFilters = localStorage.getItem(ACTIVITY_FILTER_KEY);
  if (savedFilters) {
    const preference = JSON.parse(savedFilters);
    return {
      ...preference,
      filterDateRange: {
        fromDate: moment(preference.filterDateRange.fromDate),
        toDate: moment(preference.filterDateRange.toDate),
      },
    };
  }

  return defaultFilter;
};

export const getCallLogSavedFilters = (): LogFilterParam => {
  const savedFilters = localStorage.getItem(CALL_LOG_FILTERS_KEY);
  if (savedFilters) {
    const preference = JSON.parse(savedFilters);
    return {
      ...preference,
      filterDateRange: {
        fromDate: moment(preference.filterDateRange.fromDate),
        toDate: moment(preference.filterDateRange.toDate),
      },
    };
  }

  return defaultCallLogFilters;
};

export const getTaskSavedFilters = (): TaskFilterParam => {
  const savedFilters = localStorage.getItem(TASK_FILTERS_KEY);
  if (savedFilters) {
    const preference = JSON.parse(savedFilters);
    return {
      ...preference,
      filterDateRange: {
        fromDate: moment(preference.filterDateRange.fromDate),
        toDate: moment(preference.filterDateRange.toDate),
      },
    };
  }

  return defaultTaskFilters;
};

export const getTaskSavedFiltersWithTagsValidation = async (dispatch: AppThunkDispatch, partnerId: number): Promise<TaskFilterParam> => {
  const savedFilters = localStorage.getItem(TASK_FILTERS_KEY);

  if (savedFilters) {
    const preference = JSON.parse(savedFilters);
    let updatedTaskTagsFilters = [...preference.filterTaskTags];

    if (!isEmpty(preference.filterTaskTags)) {
      await dispatch(getPartnerTaskTags(partnerId)).then(tags => {
        if (tags) {
          const tagsKey = tags.map(tag => tag.key);
          const obsoleteTagsToRemove = difference(preference.filterTaskTags, [UNTAGGED_TASKS, ...tagsKey]);

          if (!isEmpty(obsoleteTagsToRemove)) {
            updatedTaskTagsFilters = pullAll(preference.filterTaskTags, obsoleteTagsToRemove);
          }
        }
      });
    }

    return {
      ...preference,
      filterTaskTags: updatedTaskTagsFilters,
      filterDateRange: {
        fromDate: moment(preference.filterDateRange.fromDate),
        toDate: moment(preference.filterDateRange.toDate),
      },
    };
  }
  return defaultTaskFilters;
};

export const getTaskFilter = (filterParam: TaskFilterParam): QTaskFilter => {
  const { filterTaskStatus, filterTaskTags, filterTaskPriorities, filterDateValue, filterDateRange, householdQID, sortDirection } = filterParam;

  const filters: QTaskFilter = {
    'dueDate[orderby]': sortDirection,
    limit: 250,
  };

  if (filterTaskStatus && !isEmpty(filterTaskStatus)) {
    filters.status = filterTaskStatus.toString();
  }

  if (filterTaskTags && !isEmpty(filterTaskTags)) {
    filters.taskTags = filterTaskTags.toString();
  }

  if (filterTaskPriorities && !isEmpty(filterTaskPriorities)) {
    filters.priority = filterTaskPriorities.toString();
  }

  if (filterDateValue !== ALL_DATE_RANGE) {
    filters['dueDate[dateafter]'] = filterDateRange?.fromDate.format(dateFormat);
    filters['dueDate[datebefore]'] = filterDateRange?.toDate.format(dateFormat);
  }

  if (householdQID) {
    filters.householdQID = householdQID;
  }

  return filters;
};

const filterDuplicateTasks = (taskList: QTask[]): QTask[] => {
  const taskIds: Array<number> = [];
  const filteredTasks: QTask[] = [];
  taskList.forEach(task => {
    if (task.id && !taskIds.includes(task.id)) {
      taskIds.push(task.id);
      filteredTasks.push(task);
    }
  });
  return filteredTasks;
};

/*
  Retrieving createdBy tasks needs to be in a separate API call than assignee tasks.
  Joining the two it's possible to get duplicate tasks (a task created and assigned to a user), hence the filterDuplicateTasks
 */
export const getTasksByExtendedFilters = (qFeedTaskFilterParam: QFeedTaskFilterParam): Promise<Array<QTask>> => {
  const promises: Array<Promise<Array<QTask>>> = [];

  const taskFilter = getTaskFilter(qFeedTaskFilterParam);
  const { userEmail } = qFeedTaskFilterParam;

  const assignee: Array<string> = [];
  qFeedTaskFilterParam.filterTaskResponsibilities.forEach(responsibility => {
    if (responsibility === CREATED_BY_ME_RESPONSIBILITY.value) {
      promises.push(getAllTasks({ ...taskFilter, createdBy: userEmail }));
    } else if (responsibility === ASSIGN_TO_ME_RESPONSIBILITY.value) {
      assignee.push(userEmail);
    } else {
      assignee.push(responsibility);
    }
  });

  if (assignee.length > 0) {
    promises.push(getAllTasks({ ...taskFilter, assignee: assignee.toString() }));
  }

  return Promise.all(promises).then(flattenList).then(filterDuplicateTasks);
};

export const getCallLogFilter = (filterParam: LogFilterParam): CallLogFilter => {
  const { filterDateValue, filterDateRange, filterLogSubject, householdQID, sortDirection } = filterParam;
  const filters: CallLogFilter = {};
  if (filterDateValue !== ALL_DATE_RANGE) {
    filters['meetingDate[dateafter]'] = filterDateRange.fromDate.format(dateFormat);
    filters['meetingDate[datebefore]'] = filterDateRange.toDate.format(dateFormat);
  }
  if (filterLogSubject !== ALL_LOG_SUBJECTS) {
    filters.title = filterLogSubject;
  }
  if (householdQID) {
    filters.householdQID = householdQID;
  }

  filters['meetingDate[orderby]'] = sortDirection;
  filters['limit'] = 250;

  return filters;
};

export const getMessageFilter =
  (activityFilters: ActivityFiltersWithoutQuery) =>
  (messages: Array<QMessageMetadata>): Array<QMessageMetadata> =>
    messages.filter(
      message => activityFilters.filterDateValue === ALL_DATE_RANGE || isBetweenDates(activityFilters.filterDateRange, moment(message.date)),
    );

export const qtaskSearch = (query: string, task: QTask): boolean =>
  caseInsensitiveFuzzySearch(query, [
    task.title,
    task.details,
    task.householdName,
    task.householdQID,
    task.dueDate,
    task.dueDate ? moment(task.dueDate).format(ENG_DATE_FORMAT) : 'N/A',
    `${task.householdName ?? ''} ${task.title}`,
    task.taskTags ? task.taskTags.map(tag => tag.key).join(' ') : '',
  ]);

export const callLogSearch = (query: string, callLog: QLog): boolean =>
  caseInsensitiveFuzzySearch(query, [
    callLog.title,
    callLog.content,
    callLog.householdName,
    callLog.owners && callLog.owners.length > 0 ? callLog.owners[0] : '',
    callLog.date,
    moment(callLog.date).format(ENG_DATE_FORMAT),
    `${callLog.householdName ?? ''} ${callLog.title}`,
  ]);

export const eventSearch = (query: string, event: CalendarEvent): boolean =>
  caseInsensitiveFuzzySearch(query, [event.body?.content, event.subject?.label, event.subject?.value]);

export const emailSearch = (query: string, message: QMessageMetadata): boolean =>
  caseInsensitiveFuzzySearch(query, [
    message.subject,
    message.from,
    message.notes,
    message.date,
    moment(message.date).format(ENG_DATE_FORMAT),
    ...message.to,
  ]);

export const sortActivitiesByDate =
  (direction?: SortDirection) =>
  (a: Activity, b: Activity): number => {
    const endDateA = getEndDate(a);
    const endDateB = getEndDate(b);

    const diff = endDateB.diff(endDateA);
    return direction === 'ASC' ? -1 * diff : diff;
  };
